import { Texture } from "@/core/backends/webgl/texture"
import { mat3, vec3 } from "gl-matrix"
import { fitToSize } from "@/core/utils/resize-image"
import { Renderer } from "@/core/pipeline/renderer"

/**
 * Code Ref:
 * https://github.com/Beep6581/RawTherapee/blob/dev/rtengine/hilite_recon.cc#L1348
 * https://github.com/darktable-org/darktable/blob/a4415afa73988078b60b4b833f4c65bf69798d99/src/iop/hlreconstruct/opposed.c#L93
 *
 * Inpaint opposed method: Step 1, we compute refavg for one of red, green or blue, which is defined as means of both other color channels (opposing).
 * The basic idea / observation is that the refavg is a good estimate for any clipped color channel in the vast majority of images, working mostly
 * fine both for small specular highlighted spots and large areas. Step 2, the correction via some sort of global chrominance further helps to
 * correct color casts. The chrominace data are taken from the areas morphologically very close to clipped data.
 * If option.computeChrominan is false, when ignore step 2, which is much faster since cpu computation is not needed.
 */

export interface HighlightsReconOptions {
	wbCoeffs: vec3
	camToXYZ: mat3
	computeChrominan: boolean
}

interface FillShaderOptions extends HighlightsReconOptions {
	chrominan: vec3
	clips: vec3
}

export class HighlightsReconstruction {
	public render(
		inputTexture: Texture,
		outputTexture: Texture,
		renderer: Renderer,
		option: HighlightsReconOptions,
	) {
		const clipThresh = 0.987 // number from dark table
		const clips = option.wbCoeffs.map((val) => val * clipThresh) as vec3

		let chrominan: vec3 = [0, 0, 0]

		if (option.computeChrominan) {
			chrominan = this.computeChrominan(inputTexture, renderer, clips)
		}

		// Fill the clipped pixels with refAve + chrominan, and convert to proPhoto space
		this.fillHighlights(inputTexture, outputTexture, renderer, { ...option, chrominan, clips })

		return outputTexture
	}

	private computeChrominan(inputTexture: Texture, renderer: Renderer, clips: vec3) {
		const scratchTexture = renderer.scratchTexture
		const maskBuffer = renderer.dualBuffers.readBuffer

		const width2 = inputTexture.width >> 2
		const height2 = inputTexture.height >> 2
		const maxSize = Math.min(width2, height2, 800)
		const [width, height] = fitToSize(
			[inputTexture.width, inputTexture.height],
			[maxSize, maxSize],
		) as [number, number]

		maskBuffer.updateSize(width, height)
		scratchTexture.update({ format: "rgba32float", width, height })

		// 1. get clipping mask: clipped channel is 1, otherwise 0
		this.getClipMask(inputTexture, maskBuffer, renderer, clips)

		// 2. dilate mask - nearby unclipped pixels denoted as 2, and compute chrominan for nearby unclipped channel.
		// chrominan = color - refAve, where refAve are 3x3 mean of opposing channels.
		this.computeUnclippedChrominan(inputTexture, maskBuffer, scratchTexture, renderer, clips)

		// 3. compute average chrominan in cpu
		const pixels = scratchTexture.read()
		return this.getAverageChrominan(pixels as Float32Array)
	}

	private getAverageChrominan(pixels: Float32Array): vec3 {
		const ave: vec3 = [0, 0, 0]
		const count: vec3 = [0, 0, 0]
		const n = pixels.length
		const thresh = 100 // because invalid value is denoted as 1000 in shader
		for (let i = 0; i < n; i += 4) {
			if (<number>pixels[i + 3] > 0) {
				const r = pixels[i] as number
				const g = pixels[i + 1] as number
				const b = pixels[i + 2] as number
				if (r < thresh) {
					ave[0] += r
					count[0]++
				}
				if (g < thresh) {
					ave[1] += g
					count[1]++
				}
				if (b < thresh) {
					ave[2] += b
					count[2]++
				}
			}
		}
		for (let i = 0; i < 3; i++) {
			ave[i] = <number>count[i] < 20 ? 0 : <number>ave[i] / <number>count[i]
		}
		return ave
	}

	private getClipMask(inputTexture: Texture, out: Texture, renderer: Renderer, clips: vec3) {
		out.bindFramebuffer()

		inputTexture.bind(0)
		renderer.shaders.hiliteReconClipMask.render({
			inputTexture: 0,
			clips: clips,
		})

		return out
	}

	private computeUnclippedChrominan(
		inputTexture: Texture,
		maskTexture: Texture,
		out: Texture,
		renderer: Renderer,
		clips: vec3,
	) {
		out.bindFramebuffer()

		inputTexture.bind(0)
		maskTexture.bind(1)

		renderer.shaders.hiliteReconChrominan.render({
			inputTexture: 0,
			maskTexture: 1,
			clips: clips,
		})

		return out
	}

	private fillHighlights(
		inputTexture: Texture,
		out: Texture,
		renderer: Renderer,
		option: FillShaderOptions,
	) {
		out.bindFramebuffer()

		inputTexture.bind(0)

		renderer.shaders.hiliteReconFill.render({
			inputTexture: 0,
			wbCoeffs: option.wbCoeffs,
			clips: option.clips,
			chrominan: option.chrominan,
			camToXYZ: option.camToXYZ,
		})

		return out
	}
}
